import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import todAnalytics from '~/analytics/tod/todAnalytics'
import { TodButtonEventName } from '~/analytics/tod/todEvents.types'

import BookingFormWrapper from '~/booking/components/BookingFormWrapper/BookingFormWrapper'
import ROUTES from '~/shared/constants/routes.const'
import ReduxPersistor from '~/redux/utils/ReduxPersistor'
import TutorForSelfOrderPayloadAdapter from '~/booking/services/TutorForSelfOrderPayloadAdapter'
import useIsValidTutorForSelfCart from '~/booking/hooks/useIsValidTutorForSelfCart'
import useProcessCartItems from '~/booking/hooks/useProcessCartItems'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { CheckoutScenario } from '~/types/booking'
import { selectBookingForSelfFormErrors, selectTutorForSelfCart, updateCartFromPreference } from '~/redux/slices/cart'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { setCheckoutScenarioActionCreator } from '~/redux/slices/checkout'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { BookingForSelfFormFields } from '~/booking/components/BookingCustomizationForm/BookingForSelfFormFields'
import useBookingForSelf from '~/booking/pages/booking-for-self/BookingForSelf.hook'
import { useGetPreferenceQuery } from '~/redux/apis/preference-api'

import useCheckBookingForSelfFormError from './useBookingForSelfFormError'

export default function BookingForSelf(): React.ReactElement {
  const { t } = useTranslation('booking')
  const router = useRouter()

  const cart = useAppSelector(selectTutorForSelfCart)
  const bookingForSelfFormErrors = useAppSelector(selectBookingForSelfFormErrors)
  const { isFormValid } = useCheckBookingForSelfFormError()
  const isValidCart = useIsValidTutorForSelfCart()

  const authenticated = useAppSelector(isAuthenticated)
  const { processCartItems, isLoading, isSuccess, isUninitialized } = useProcessCartItems()
  const { data: preference } = useGetPreferenceQuery()
  const dispatch = useAppDispatch()

  const {
    handleOnLanguageChange,
    handleOnLevelsOfEducationClick,
    handleOnLocationChange,
    handleOnPhysicalLocationTypeClick,
    handleOnProductClick,
    handleOnSubjectClick,
  } = useBookingForSelf()

  useEffect(() => {
    if (isSuccess) {
      dispatch(setCheckoutScenarioActionCreator(CheckoutScenario.TUTOR_FOR_SELF))
      router.push(ROUTES.BOOKING_CHECKOUT)
    }
  }, [router, isSuccess, dispatch])

  useEffect(() => {
    void (async () => {
      if (authenticated && router.query.didAuthenticate && isValidCart && isUninitialized) {
        await processCartItems(new TutorForSelfOrderPayloadAdapter(cart))

        // Discard the query params.
        await router.replace(ROUTES.BOOKING_FOR_SELF, undefined, {
          shallow: true,
        })
      }
    })()
  }, [cart, isValidCart, processCartItems, router, authenticated, isUninitialized])

  useEffect(() => {
    if (preference) {
      dispatch(updateCartFromPreference(preference))
    }
  }, [dispatch, preference])

  const handleOnGoToCheckoutClick = async (): Promise<void> => {
    const validForm = isFormValid()

    if (!validForm) {
      return
    }

    todAnalytics.logButtonClick(TodButtonEventName.BOOK_A_TUTOR_FOR_SELF_CHECKOUT)

    if (!authenticated) {
      router.push({
        pathname: ROUTES.LOGIN,
        query: {
          from: 'booking-for-self',
        },
      })
    }

    if (authenticated && isValidCart) {
      await processCartItems(new TutorForSelfOrderPayloadAdapter(cart))
    }
  }

  const goToCheckoutButton = (): React.ReactElement => {
    return (
      <ButtonPrimary
        className="ml-auto"
        isFullWidth
        isLoading={isLoading}
        onClick={handleOnGoToCheckoutClick}
        size="small"
      >
        {t('buttonGoToCheckout')}
      </ButtonPrimary>
    )
  }

  return (
    <BookingFormWrapper isLoading={isLoading}>
      <h1 className="text-h3 ">{t('title')}</h1>
      <ReduxPersistor>
        {goToCheckoutButton()}

        <BookingForSelfFormFields
          errors={bookingForSelfFormErrors}
          form={cart}
          onLanguageChange={handleOnLanguageChange}
          onLevelsOfEducationClick={handleOnLevelsOfEducationClick}
          onLocationChange={handleOnLocationChange}
          onPhysicalLocationTypeClick={handleOnPhysicalLocationTypeClick}
          onProductClick={handleOnProductClick}
          onSubjectClick={handleOnSubjectClick}
        />

        {goToCheckoutButton()}
      </ReduxPersistor>
    </BookingFormWrapper>
  )
}
